import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { ActionModalHost } from './ActionModalHost';
import type { PharmacyAction, PharmacyRef } from './types';

interface PharmacyActionsContextValue {
  openAction: (action: PharmacyAction, pharmacy: PharmacyRef) => void;
}

const PharmacyActionsContext = createContext<PharmacyActionsContextValue | null>(null);

/**
 * Single owner of all pharmacy management modals. Both the grid Actions menu
 * and the details page open the same focused dialogs through this context.
 */
export function PharmacyActionsProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<{ action: PharmacyAction; pharmacy: PharmacyRef } | null>(null);

  const openAction = useCallback((action: PharmacyAction, pharmacy: PharmacyRef) => setActive({ action, pharmacy }), []);
  const close = useCallback(() => setActive(null), []);
  const value = useMemo(() => ({ openAction }), [openAction]);

  return (
    <PharmacyActionsContext.Provider value={value}>
      {children}
      {active && <ActionModalHost action={active.action} pharmacy={active.pharmacy} onClose={close} />}
    </PharmacyActionsContext.Provider>
  );
}

export function usePharmacyActions(): PharmacyActionsContextValue {
  const ctx = useContext(PharmacyActionsContext);
  if (!ctx) throw new Error('usePharmacyActions must be used within PharmacyActionsProvider');
  return ctx;
}
